import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { ShimmerText, ShimmerSectionHeader } from 'react-shimmer-effects'
import {
  DownloadPdfIcon,
  useErrorService,
  useUserService,
  downloadTravelChecklistPdf,
  splitChecklistSections,
} from '@abroad/components'
import classNames from 'classnames'
import { PageHeader } from '../common'
import API from '../../utils/API'
import DocumentIcon from '../assetsComponents/DocumentIcon'
import { gtmViewExperienceTravelChecklistAction } from './gtmfunctions'

const YourQuestsToDo = () => {
  const history = useHistory()
  const Error = useErrorService()
  const { user } = useUserService()
  const [checklistData, setChecklistData] = useState(null)
  const [checklistProgress, setChecklistProgress] = useState({})
  const [questTitle, setQuestTitle] = useState('')
  const { questId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const hasChecklistContent =
    checklistData?.description ||
    (checklistData?.sections?.length !== 0 &&
      checklistData?.sections?.[0] &&
      (checklistData.sections[0].title ||
        checklistData.sections[0].notes ||
        (checklistData.sections[0].items &&
          checklistData.sections[0].items.length > 0)))

  useEffect(() => {
    const getQuestsToDo = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.quests.questsGetChecklist(questId)
        setChecklistData(data.travelChecklist)
        setChecklistProgress(data.checklistProgress || {})
        if (data?.questTitle) {
          setQuestTitle(data?.questTitle)
        }
        setIsLoading(false)
      } catch (e) {
        if (e?.code === 'resource_not_found') {
          history.replace('/your-experiences')
        } else {
          Error.showError(e)
          setIsLoading(false)
        }
      }
    }
    getQuestsToDo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  useEffect(() => {
    if (questId && checklistData) {
      // GTM tracking for experience travel checklist view
      gtmViewExperienceTravelChecklistAction(
        questId,
        'Experience Travel Checklist',
        user?._id,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, checklistData])

  const ShimmerEffect = () => {
    return (
      <Row className='my-4'>
        <Col md={6}>
          <ShimmerSectionHeader />
          <ShimmerText line={5} gap={10} />
        </Col>
        <Col md={6}>
          <ShimmerSectionHeader />
          <ShimmerText line={5} gap={10} />
        </Col>
      </Row>
    )
  }

  const handleCheckBox = async (itemId) => {
    const originalProgress = { ...checklistProgress }
    try {
      const checked = !checklistProgress[itemId]
      const updatedProgress = {
        ...checklistProgress,
        [itemId]: checked,
      }
      setChecklistProgress(updatedProgress)

      await API.quests.questsToDoPut(questId, {
        itemId: itemId,
        checked: checked,
      })
    } catch (e) {
      if (e?.code === 'resource_not_found') {
        history.replace('/your-experiences')
      } else {
        setChecklistProgress(originalProgress)
        Error.showError(e)
      }
    }
  }

  const handleDownloadPdf = async () => {
    if (!hasChecklistContent || isDownloadingPdf) {
      return
    }
    setIsDownloadingPdf(true)
    try {
      await downloadTravelChecklistPdf({
        questTitle: questTitle,
        checklistData,
        checklistProgress,
      })
    } catch (e) {
      Error.showError({
        message: 'Unable to download travel checklist PDF. Please try again.',
      })
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{'Travel Checklist | Abroad'}</title>
        <meta name='title' content={'Travel Checklist | Abroad'}></meta>
        <meta property='og:title' content={'Travel Checklist | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Travel Checklist | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/quests`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/quests`}></meta>
      </Helmet>
      <div>
        <PageHeader
          showBackArrow={true}
          version='small'
          title='Your Experiences'
          backArrowAction={() => history.goBack()}
        />
        <div className='d-flex justify-content-between align-items-center py-16px gap-3'>
          <div className='d-flex align-items-center gap-3'>
            <h1 className='s1 mb-0'>Travel checklist</h1>
            {!isLoading && hasChecklistContent && (
              <OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id='download-travel-checklist-tooltip'>
                    Download PDF
                  </Tooltip>
                }>
                <span className='d-inline-flex align-items-center'>
                  <DownloadPdfIcon
                    onClick={handleDownloadPdf}
                    disabled={isDownloadingPdf}
                    isLoading={isDownloadingPdf}
                    className={classNames('d-flex align-self-center', {
                      'cursor-pointer': !isDownloadingPdf,
                    })}
                  />
                </span>
              </OverlayTrigger>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className='mt-4'>
            <ShimmerText line={3} gap={10} />
            <ShimmerEffect />
          </div>
        ) : hasChecklistContent ? (
          <>
            <div
              className='s8'
              dangerouslySetInnerHTML={{ __html: checklistData.description }}
            />
            <Row className='mt-20px align-items-start'>
              {(() => {
                const { firstHalf, secondHalf } = splitChecklistSections(
                  checklistData.sections || [],
                )

                const renderSection = (section) => (
                  <div key={section._id} className='mb-20px'>
                    <div className='d-flex align-items-center position-relative display-inline-block pb-5px mb-1rem'>
                      <DocumentIcon />
                      <h2 className='text-uppercase s6 mb-0 ms-2'>
                        {section.title}
                      </h2>
                    </div>
                    <div className='border border-dark border-radius-10 p-3 mt-12px d-flex flex-column'>
                      <ul className='list-unstyled s8 mb-0'>
                        {section.items?.map((item, idx) => (
                          <li
                            key={item._id}
                            className={`d-flex ${
                              idx === section.items.length - 1
                                ? 'mb-0'
                                : 'mb-14px'
                            }`}
                            onClick={() => handleCheckBox(item._id)}>
                            <div className='me-3 cursor-pointer'>
                              {checklistProgress[item._id] ? (
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='20'
                                  height='20'
                                  viewBox='0 0 20 20'
                                  fill='none'>
                                  <rect
                                    x='0.5'
                                    y='0.5'
                                    width='19'
                                    height='19'
                                    rx='4.5'
                                    fill='#509F92'
                                  />
                                  <rect
                                    x='0.5'
                                    y='0.5'
                                    width='19'
                                    height='19'
                                    rx='4.5'
                                    stroke='#509F92'
                                  />
                                  <path
                                    d='M8.66182 14.2665C8.55273 14.3762 8.40398 14.4374 8.2494 14.4374C8.09482 14.4374 7.94607 14.3762 7.83698 14.2665L4.54348 10.9724C4.20165 10.6306 4.20165 10.0764 4.54348 9.7352L4.9559 9.32278C5.29773 8.98095 5.85132 8.98095 6.19315 9.32278L8.2494 11.379L13.8056 5.82278C14.1475 5.48095 14.7016 5.48095 15.0429 5.82278L15.4553 6.2352C15.7972 6.57703 15.7972 7.1312 15.4553 7.47245L8.66182 14.2665Z'
                                    fill='white'
                                  />
                                </svg>
                              ) : (
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='20'
                                  height='20'
                                  viewBox='0 0 20 20'
                                  fill='none'>
                                  <rect
                                    x='0.5'
                                    y='0.5'
                                    width='19'
                                    height='19'
                                    rx='4.5'
                                    fill='white'
                                  />
                                  <rect
                                    x='0.5'
                                    y='0.5'
                                    width='19'
                                    height='19'
                                    rx='4.5'
                                    stroke='#010101'
                                  />
                                </svg>
                              )}
                            </div>
                            <span className='cursor-pointer'>{item.item}</span>
                          </li>
                        ))}
                      </ul>
                      {section.notes && (
                        <div className='s8 opacity-50 mt-3'>
                          <div
                            dangerouslySetInnerHTML={{ __html: section.notes }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )

                return (
                  <>
                    <Col md={6}>{firstHalf.map(renderSection)}</Col>
                    <Col md={6}>{secondHalf.map(renderSection)}</Col>
                  </>
                )
              })()}
            </Row>
          </>
        ) : (
          <div className='text-center my-5'>
            <h4 className='text-center s5'>
              There is no Travel checklist available at the moment.
            </h4>
          </div>
        )}
      </div>
    </>
  )
}

export default YourQuestsToDo
